/**
 * NX-2798 — the metric glossary: one plain-English explanation per metric label.
 *
 * Papa's report (2026-08-26): "all the metrics on the portfolio page have no explanation as to what
 * they mean so a client will have to guess." The audience includes sponsors and client-viewers who
 * are not delivery specialists; a figure nobody can interpret is a figure nobody trusts.
 *
 * KEYED BY THE RENDERED LABEL, deliberately: MetricTile looks its own label up here, so every tile
 * in the app gains its explanation with zero call-site wiring, and a future tile whose label is
 * already defined gets one for free. A label with no entry renders exactly as before — missing help
 * never breaks a tile (AC4). If a label is ever reworded, its entry moves with it or the affordance
 * silently disappears, which a test guards against for the labels that exist today.
 *
 * Copy rules: one to two sentences; say what the number MEANS and what direction is good; state the
 * honesty semantics where they matter (N/A = unobserved, never zero; confidence = evidence-weighted,
 * never a guess). No jargon a sponsor would have to look up.
 */

export const METRIC_HELP: Record<string, string> = {
  // ── Flow (how work moves) ──────────────────────────────────────────────────
  "Lead time p85":
    "How long work takes from when it first appears to when it's done — 85% of recent items finished within this time. Lower is faster delivery.",
  "Cycle time p85":
    "How long work takes once someone actually starts on it — 85% of recent items finished within this time of active work beginning. The gap between this and lead time is waiting.",
  Throughput:
    "How many work items were completed in the recent window, expressed per week. The engine behind the delivery forecast.",
  WIP: "Work in progress: how many items are started but not finished right now. Sustained high WIP usually means things are stuck, not that more is getting done.",
  "Flow efficiency":
    "Of the total time work spends in the system, the share that is active work rather than waiting. 100% is implausible for real teams — waiting in review or queues is normal; very low values point to bottlenecks.",
  "Oldest in-flight":
    "The age of the longest-running unfinished item. A quietly ageing item is often blocked or forgotten — this surfaces it.",

  // ── Forecast & estimation ─────────────────────────────────────────────────
  "P50 completion":
    "The date the remaining work has a 50/50 chance of being done by, simulated from the team's own recent throughput — a coin-flip date, not a commitment.",
  "P85 completion":
    "The date the remaining work is 85% likely to be done by, from the same simulation. A safer date to plan around than P50.",
  "Captured estimates":
    "How many estimate declarations (e.g. story points) have been observed from your delivery sources, including revisions.",
  "Estimated work items":
    "How many distinct work items carry an estimate. Coverage here is what makes estimate-based views credible.",
  "Estimate kinds":
    "The types of estimate observed (points, time, t-shirt). SeneSight never converts between kinds — they are shown side by side.",

  "Estimate accuracy":
    "How close estimates land to reality, as the mean ratio of actual to estimated effort across completed items. 1.0× is perfectly calibrated; the trend matters more than any single value.",
  "Estimate bias":
    "The systematic direction estimates lean — consistently over- or under-estimated — separate from accuracy. Balanced means no persistent lean either way.",
  "Estimate spread":
    "How consistent estimation is item-to-item. A wide spread means individual estimates are unreliable even when the average looks fine.",
  "Estimate-derived date":
    "The completion date implied by the declared estimates and the team's observed pace through them.",
  "Evidence-based forecast date":
    "The completion date simulated purely from observed throughput, with no estimates involved. Shown beside the estimate-derived date — the two are never blended.",
  "Say/do ratio":
    "Of the work committed to a period, the share actually delivered in that period. The commitment-reliability signal behind planning credibility.",
  "Roadmap horizon":
    "How far ahead the roadmap projects, bounded by how much observed history exists to support it.",

  // ── DORA (delivery performance) ───────────────────────────────────────────
  "Deploy frequency":
    "How often code reaches production, per day, over the recent window. More frequent, smaller deploys generally mean lower risk per change.",
  "Lead time for change":
    "The time from a commit being written to that change running in production. Measures the delivery pipeline, not the work itself.",
  "Change failure rate":
    "The share of production deployments whose checks failed. Counted only from deploys with a decisive outcome — an unobservable deploy is never assumed good or bad.",
  "Change-failure rate":
    "The share of production deployments whose checks failed. Counted only from deploys with a decisive outcome — an unobservable deploy is never assumed good or bad.",
  "Time to restore":
    "After a failed deployment, how long until the next successful one restored service. Only measurable once a failure has actually been observed.",
  "Deploys with no decisive outcome":
    "Deployments SeneSight could not correlate to a pass or fail signal. Shown separately because 'we couldn't tell' is different from 'it worked' — they are never folded into the failure rate.",

  // ── Reliability ───────────────────────────────────────────────────────────
  "Mean time to resolve":
    "Average time from an incident being raised to it being resolved. N/A until an incident-management source is connected — unobserved is not the same as zero incidents.",
  Incidents:
    "Incidents observed in the window from a connected incident-management source. N/A means no such source is connected, not that nothing went wrong.",
  "SLO adherence":
    "How well the service met its declared service-level objectives. N/A until an SLO source is connected — absence of measurement is not full adherence.",

  // ── Friction (where time leaks) ───────────────────────────────────────────
  "CI queue wait":
    "Time changes spend waiting for their first CI signal after being opened. Persistent waits here slow every single change.",
  "Flaky retry cost":
    "Time burned re-running CI checks that eventually passed — the tax paid to flaky tests and infrastructure.",
  "Review wait":
    "Time changes spend waiting for review. Usually the single largest share of lead time, and the cheapest to improve.",
  "Dependency wait":
    "Time work spends blocked on other teams or systems. N/A until a dependency-tracking source is connected — unobserved is not zero waiting.",
  "Lost capacity":
    "All measured waiting converted into work items' worth of lost delivery, using the team's own throughput as the exchange rate. Roughly: what the friction above costs per window.",

  // ── Capability & coverage ─────────────────────────────────────────────────
  "Capability coverage":
    "How much of the declared engineering practice (from the capability questionnaire) is backed by observed evidence, and where the two disagree.",

  // ── Portfolio-level ───────────────────────────────────────────────────────
  "Rolled-up forecast":
    "The blended completion outlook across this program's sub-projects, weighted by each one's own evidence.",
  "Data confidence":
    "How much observed evidence stands behind the figures on this page, weighted by freshness. Low confidence means thin or ageing evidence — the numbers are honest but lightly supported.",
  Status:
    "Overall health rolled up from the worst observed dimension (recent changes, CI, deployments), capped unless a credible delivery forecast exists. Green is earned from evidence, never assumed.",

  // ── Data coverage (E12.6) ─────────────────────────────────────────────────
  "Data coverage":
    "What SeneSight has actually observed for this project, per connected source — and what a source could report but hasn't yet. A gap here means 'not yet observed', never 'zero activity'; scores only ever use what was observed.",

  // ── Health dimensions (the drill-in tiles) ────────────────────────────────
  "Change recency":
    "Is code actually changing? Judged from the freshest observed commit or change — a project nobody touches drifts to warning, then stale.",
  "CI outcomes":
    "Are the checks passing? The recent pass rate of observed CI outcomes for this project's changes.",
  "Deploy outcomes":
    "Did the last deployment succeed? Judged by correlating the most recent deployment to its check results — an uncorrelatable deploy reads as unknown, never as a failure.",
};

/** The explanation for a rendered label, or undefined — an unknown label renders unchanged. */
export const helpFor = (label: string): string | undefined => METRIC_HELP[label];
